import { useEffect, useMemo, useState } from 'react';
import { HTMLReactParserOptions } from 'html-react-parser';
import SkeletonLoader from '../../../../Global/SkeletonLoader';
import { Info, Warning } from '@phosphor-icons/react';
import { Capacitor } from '@capacitor/core';
import { ImageSafetyTag } from '../../../../../../types/Image';

import { getSensitiveContentTooltipText } from '../../../../../../utils/getSensitiveContentTooltipText';
import { Switch } from '@/components/ui/switch';
import { updateCurrentUserInfo } from '../../../../../../services/auth';
import { RootState } from '../../../../../../store/store';
import { useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { parseHtmlWithMathLatex } from '../../../../../../utils/htmlParser';
import {
  getCachedImageDimensions,
  setCachedImageDimensions,
} from '../../../../../../utils/imageDimensionCache';
import Tooltip from '@/components/ui/tooltip';

const NSFW_TAGS: ImageSafetyTag[] = ['nudity', 'graphic', 'pediatric'];

interface CustomImageProps {
  src?: string;
  caption?: string;
  credit?: string;
  annotatedSrc?: string;
  nsfw?: ImageSafetyTag[];
}

const CustomImage: React.FC<CustomImageProps> = ({
  src,
  caption,
  credit,
  annotatedSrc,
  nsfw = [],
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const [isLoaded, setIsLoaded] = useState<boolean>(() => !src);

  const isSensitiveContent = useMemo(
    () => nsfw.some((tag) => NSFW_TAGS.includes(tag)),
    [nsfw]
  );

  const isNativePlatform = Capacitor.isNativePlatform();
  const isBlockedOnMobileApps = isSensitiveContent && isNativePlatform;

  const [isNsfwOverlayVisible, setIsNsfwOverlayVisible] =
    useState<boolean>(isSensitiveContent);

  const shouldShowNsfwOverlay =
    isNsfwOverlayVisible && !currentUser?.showSensitiveImages;

  useEffect(() => {
    setIsNsfwOverlayVisible(isSensitiveContent);
  }, [src, isSensitiveContent]);

  useEffect(() => {
    if (!src) {
      setIsLoaded(true);
      return;
    }

    setIsLoaded(false);

    const preloader = new Image();
    preloader.src = src;

    const cacheDimensions = () =>
      setCachedImageDimensions(
        src,
        preloader.naturalWidth,
        preloader.naturalHeight
      );

    const handleLoad = () => {
      cacheDimensions();
      setIsLoaded(true);
    };

    const handleError = () => setIsLoaded(true);

    if (preloader.complete) {
      cacheDimensions();
      setIsLoaded(true);
    } else {
      preloader.addEventListener('load', handleLoad);
      preloader.addEventListener('error', handleError);
    }

    return () => {
      preloader.removeEventListener('load', handleLoad);
      preloader.removeEventListener('error', handleError);
    };
  }, [src]);

  const processLinks = (htmlString: string): React.ReactNode => {
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode.type === 'tag' && domNode.name === 'a' && domNode.attribs) {
          if (!domNode.attribs.target) {
            domNode.attribs.target = '_blank';
          }
        }
      },
    };

    return parseHtmlWithMathLatex(htmlString, options);
  };

  const renderedCaption = useMemo(
    () => (caption ? processLinks(caption) : null),
    [caption]
  );

  const renderedCredit = useMemo(
    () => (credit ? processLinks(credit) : null),
    [credit]
  );

  const showCaptionSkeleton = !!caption && !isLoaded;
  const showCredit = !!renderedCredit && isLoaded;
  // If we've measured this image before (this session or a previous one),
  // stamp its intrinsic width/height so the browser reserves space up front and
  // the image doesn't push content down when it loads. CSS keeps it responsive
  // (width: 100%; height: auto), so these only supply the aspect ratio.
  const cachedDimensions = getCachedImageDimensions(src);

  const handleChangeAlwaysShowSensitiveImages = async (
    showSensitiveImages: boolean
  ) => {
    const updatedUser = await updateCurrentUserInfo({
      showSensitiveImages,
    });

    if (updatedUser) {
      enqueueSnackbar(
        `Always show sensitive images ${showSensitiveImages ? 'enabled' : 'disabled'}.`,
        {
          autoHideDuration: 3000,
        }
      );
    }
  };

  const handleRevealSensitiveImage = () => {
    setIsNsfwOverlayVisible(false);
  };

  if (isBlockedOnMobileApps) {
    return null;
  }

  return (
    <div className={`custom-image-container ${!isLoaded ? 'is-loading' : ''}`}>
      <div
        className={`custom-image-container_image ${shouldShowNsfwOverlay ? 'is-nsfw' : ''}`}
      >
        {shouldShowNsfwOverlay && (
          <Tooltip title={getSensitiveContentTooltipText(nsfw)}>
            <div className="nsfw-overlay">
              <button
                type="button"
                aria-label="View sensitive content"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRevealSensitiveImage();
                }}
                style={{
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  background: 'transparent',
                  border: 0,
                  color: 'inherit',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  font: 'inherit',
                  gap: '0.5rem',
                  padding: 0,
                }}
              >
                <Warning />
                <p>
                  Sensitive Content
                  <Info />
                </p>
                <p className="click-to-view">Click to View</p>
              </button>
              <div>
                <Switch
                  className="senstive-images-toggle"
                  id="always-show-sensitive-images"
                  checked={currentUser?.showSensitiveImages ?? false}
                  onChange={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    handleChangeAlwaysShowSensitiveImages(
                      (e.target as HTMLInputElement).checked
                    );
                  }}
                  label="Always Show"
                  size="small"
                  context="modal"
                />
              </div>
            </div>
          </Tooltip>
        )}
        {!isLoaded && (
          <SkeletonLoader borderRadius="0.25rem" height="100%" margin="0" />
        )}
        {src && (
          <img
            src={src}
            alt={caption}
            width={cachedDimensions?.width}
            height={cachedDimensions?.height}
            data-image-src-annotated={annotatedSrc}
            onLoad={(event) => {
              setCachedImageDimensions(
                src,
                event.currentTarget.naturalWidth,
                event.currentTarget.naturalHeight
              );

              setIsLoaded(true);
            }}
            onError={() => setIsLoaded(true)}
            style={{ opacity: isLoaded ? 1 : 0 }}
          />
        )}
      </div>
      <div className="caption-credit">
        {showCaptionSkeleton && (
          <SkeletonLoader
            width="80%"
            height="1rem"
            borderRadius="0.25rem"
            margin="0.5rem 0"
          />
        )}
        {renderedCaption && isLoaded && (
          <div className="caption">{renderedCaption}</div>
        )}
        {showCredit && (
          <div className="credit" onClick={(event) => event.stopPropagation()}>
            {renderedCredit}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomImage;
